<template>
    <main
        class="posts-v2"
        id="js-page-content"
        role="main"
    >
        <portal to="ariane">
            <div class="tbv2-page-title">{{ pageTitle }}</div>
        </portal>

        <header class="posts-hero">
            <div class="posts-hero-text">
                <h1 class="posts-hero-title">{{ pageTitle }}</h1>
                <p class="posts-hero-sub">{{ totalLabel }}</p>
                <p v-if="pageSubtitle" class="posts-hero-note">
                    <i class="fal fa-info-circle"></i>
                    <span>{{ pageSubtitle }}</span>
                </p>
            </div>
            <MultipleListCreate class="posts-multi-list" />
        </header>

        <PostsKindTabs />

        <section class="posts-card">
            <TabPosts
                ref="tabPosts"
                :isLoadingPosts="isLoadingPosts"
                :contribution="contributionFilter"
            />
        </section>
    </main>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import TabPosts from '@/components/Model/Post/TabPosts'
import PostsKindTabs from '@/components/Model/Post/PostsKindTabs'
import { mapActions, mapState } from 'vuex'
import MultipleListCreate from '@/components/UI/MultipleListCreate'

export default {
    name: 'Posts',
    data() {
        return {
            modifStatus: false,
            divCategorie: false,
            roundedCategorie: true,
            divFormulaire: false,
            roundedFormulaire: true,
            divStatus: false,
            roundedStatus: true,
            isLoadingPosts: false
        }
    },
    components: { TabPosts, PostsKindTabs, MultipleListCreate },
    computed: {
        ...mapState('post', {
            posts: (state) => state.posts,
            postsFilters: (state) => state.postsFilters
        }),
        /**
         * L'onglet « Contributions citoyennes » est la route `/contributions` :
         * même écran, borné par le filtre serveur. La query `?contribution=`
         * reste acceptée pour les liens et favoris existants.
         */
        contributionFilter() {
            if (this.$route.name === 'Contributions') return '1'
            const kind = this.$route.query.contribution
            return kind === '1' || kind === '0' ? kind : ''
        },
        pageTitle() {
            if (this.contributionFilter === '1') return this.$t('menu.tickets.contributions')
            if (this.contributionFilter === '0') return this.$t('menu.tickets.reports_only')
            return this.$t('menu.tickets.list')
        },
        pageSubtitle() {
            return this.contributionFilter === '1'
                ? this.$t('post.page.kind_tabs.contributions_intro')
                : ''
        },
        totalLabel() {
            const n = this.posts && this.posts.total ? this.posts.total : 0
            const formatted = new Intl.NumberFormat(this.$i18n.locale).format(n)
            const key = n === 1 ? 'post.page.list_count_one' : 'post.page.list_count_other'
            return this.$t(key, { count: formatted })
        },
    },
    created() {
        let postsPromise = this.getPosts()
        let postsFiltersPromise = this.getPostsFilters()
        let statusesPromise = this.getStatus()
        Promise.all([postsPromise, postsFiltersPromise, statusesPromise]).then(
            () => {
                this.isLoadingPosts = true
            }
        )
    },
    methods: {
        ...mapActions('post', ['getPosts', 'getPostsFilters']),
        ...mapActions('status', ['getStatus'])
    }
}
</script>

<style scoped>
.posts-v2 {
    width: 100%;
    padding: 24px 32px 48px;
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2);
}

.tbv2-page-title {
    font-size: 0.95rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    letter-spacing: -0.005em;
}

.posts-hero {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--v2-space-4);
    margin-bottom: var(--v2-space-5);
}

.posts-hero-text {
    min-width: 0;
}

.posts-hero-note {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    max-width: 70ch;
    margin: 8px 0 0;
    font-size: 0.82rem;
    line-height: 1.45;
    color: #0e7490;
}

.posts-hero-title {
    margin: 0 0 6px;
    font-size: 1.75rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    letter-spacing: -0.02em;
    line-height: 1.15;
}

.posts-hero-sub {
    margin: 0;
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-3);
    line-height: 1.5;
}

.posts-multi-list {
    flex-shrink: 0;
}

.posts-card {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    overflow: hidden;
}

@media (max-width: 900px) {
    .posts-v2 {
        padding: 16px 18px 32px;
    }

    .posts-hero {
        flex-direction: column;
        align-items: stretch;
        gap: var(--v2-space-3);
    }

    .posts-hero-title {
        font-size: 1.375rem;
    }
}
</style>
